/**
 * PresentationMode — present FROM the app, not PowerPoint (Wave 12).
 *
 * Full-screen slide player over a presentations row. Slides are prose
 * (markdown) or LIVE widgets rendered by the same ChatComponentRenderer the
 * canvas uses — so drill-downs work mid-presentation: pitch the number,
 * click it, show the customer the actual transactions.
 *
 * Navigation: → / ← / Space, on-screen arrows, tap zones on mobile.
 * ESC or ✕ exits.
 */

import React, { useState, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { ChatComponentRenderer } from '../ChatWidget/components';
import './PresentationMode.css';

function PresentationMode({ presentation, onClose }) {
  const [index, setIndex] = useState(0);
  const slides = presentation?.slides || [];
  const total = slides.length;

  const next = useCallback(() => setIndex((i) => Math.min(i + 1, total - 1)), [total]);
  const prev = useCallback(() => setIndex((i) => Math.max(i - 1, 0)), []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); next(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev, onClose]);

  if (!presentation || total === 0) return null;
  const slide = slides[index] || {};

  return (
    <div className="pm-overlay">
      <div className="pm-topbar">
        <span className="pm-deck-title">{presentation.title}</span>
        <span className="pm-counter">{index + 1} / {total}</span>
        <button className="pm-close" onClick={onClose} title="Exit (Esc)">
          <X size={18} />
        </button>
      </div>

      <div className="pm-stage">
        {slide.kind === 'title' ? (
          <div className="pm-slide pm-slide-title">
            <h1>{slide.title}</h1>
            {slide.markdown && (
              <div className="pm-subtitle"><ReactMarkdown>{slide.markdown}</ReactMarkdown></div>
            )}
          </div>
        ) : slide.kind === 'widget' ? (
          <div className="pm-slide pm-slide-widget">
            {slide.title && <h2 className="pm-slide-heading">{slide.title}</h2>}
            <div className="pm-widget-frame">
              <ChatComponentRenderer
                components={[slide.ui_schema]}
                onAction={() => {}}
              />
            </div>
          </div>
        ) : (
          <div className="pm-slide pm-slide-prose">
            {slide.title && <h2 className="pm-slide-heading">{slide.title}</h2>}
            <div className="pm-prose"><ReactMarkdown>{slide.markdown || ''}</ReactMarkdown></div>
          </div>
        )}
      </div>

      <button
        className="pm-nav pm-nav-prev"
        onClick={prev}
        disabled={index === 0}
        title="Previous (←)"
      >
        <ChevronLeft size={22} />
      </button>
      <button
        className="pm-nav pm-nav-next"
        onClick={next}
        disabled={index === total - 1}
        title="Next (→)"
      >
        <ChevronRight size={22} />
      </button>

      <div className="pm-progress">
        {slides.map((_, i) => (
          <button
            key={i}
            className={`pm-dot ${i === index ? 'pm-dot-active' : ''}`}
            onClick={() => setIndex(i)}
            title={`Slide ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

export default PresentationMode;
